import React, { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router'
import { VariantShell } from '@/app/layouts/VariantShell'
import { PageWrapper } from './PageWrapper'
import { Spinner } from '@agion/ui'
import { GovernanceRouteGuard } from './GovernanceRouteGuard'
import { ProtectedRoute } from '../components/ProtectedRoute'
import { lazyWithRetry, prefetchRegisteredRoutes } from './lazyWithRetry'
import { intelligenceFeatures } from '@/shared/config/intelligence-features'
import { getRuntimeEnv } from '@/shared/config/runtime-env'
import { useUIVariant } from '@/shared/ui-variant'

const isMissionGalleryEnabled = () => getRuntimeEnv('VITE_MISSION_GALLERY_ENABLED') === 'true'

// Canonical Next resource list URLs remain usable in the current interface,
// which keeps its existing Resources page and tab addresses.
const CurrentResourceListRedirect: React.FC<{ page: 'models' | 'connections' | 'tools' }> = ({ page }) => {
  const { search, hash } = useLocation()
  return <Navigate to={{ pathname: `/governance/resources/${page}`, search, hash }} replace />
}

// Capsule creation starts with an explicit kind choice. Connected data uses
// WS3; the legacy 4-step wizard remains at /capsules/new/legacy for rollback.
// Capsule detail remains at /capsules/:capsuleId/manage.

// Loading component for lazy loaded pages (only used for routes outside MainLayout)
const PageLoader = () => (
  <div className="flex h-full items-center justify-center">
    <Spinner size="lg" />
  </div>
)

// ✅ PURE LAZY LOADING - No direct imports at all!
// Each page is loaded only when needed, creating separate bundles

// Core pages
const NotFoundPage = lazyWithRetry(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))
const UnauthorizedPage = lazyWithRetry(() => import('@/pages/UnauthorizedPage').then(m => ({ default: m.UnauthorizedPage })))

// Onboarding pages
const PendingApprovalPage = lazyWithRetry(() => import('@/pages/onboarding/PendingApprovalPage').then(m => ({ default: m.PendingApprovalPage })))
const CreateOrganizationPage = lazyWithRetry(() => import('@/pages/onboarding/CreateOrganizationPage').then(m => ({ default: m.CreateOrganizationPage })))
// Integration callback pages
const GitHubCallbackPage = lazyWithRetry(() => import('@/pages/integrations/GitHubCallbackPage').then(m => ({ default: m.GitHubCallbackPage })))

// Agent pages
const AgentsPage = lazyWithRetry(() => import('@/pages/AgentsPage').then(m => ({ default: m.AgentsPage })))
const AgentDetail = lazyWithRetry(() => import('@/pages/agents/AgentDetail').then(m => ({ default: m.AgentDetail })))
// Infrastructure pages (hosts, clusters, dashboard)
const InfrastructureDashboardPage = lazyWithRetry(() => import('@/pages/infrastructure/InfrastructureDashboardPage').then(m => ({ default: m.InfrastructureDashboardPage })))
const HostsListPage = lazyWithRetry(() => import('@/pages/infrastructure/HostsListPage').then(m => ({ default: m.HostsListPage })))
const HostDetailPage = lazyWithRetry(() => import('@/pages/infrastructure/HostDetailPage').then(m => ({ default: m.HostDetailPage })))
const EdgePackSyncStatusPage = lazyWithRetry(() => import('@/pages/infrastructure/EdgePackSyncStatusPage').then(m => ({ default: m.EdgePackSyncStatusPage })))
const ClustersListPage = lazyWithRetry(() => import('@/pages/infrastructure/ClustersListPage').then(m => ({ default: m.ClustersListPage })))
const CreateClusterWizardPage = lazyWithRetry(() => import('@/pages/infrastructure/CreateClusterWizardPage').then(m => ({ default: m.CreateClusterWizardPage })))
const ClusterDetailPage = lazyWithRetry(() => import('@/pages/infrastructure/ClusterDetailPage').then(m => ({ default: m.ClusterDetailPage })))

// Mission pages
const MissionCommand = lazyWithRetry(() => import('@/pages/MissionCommand').then(m => ({ default: m.MissionCommand })))
const MissionGalleryPage = lazyWithRetry(() => import('@/pages/MissionGalleryPage').then(m => ({ default: m.MissionGalleryPage })))
const MissionDesignerV4Page = lazyWithRetry(() => import('@/pages/MissionDesignerV4Page').then(m => ({ default: m.MissionDesignerV4Page })))
const MissionPlannerPage = lazyWithRetry(() => import('@/pages/MissionPlannerPage').then(m => ({ default: m.MissionPlannerPage })))
const MissionDetail = lazyWithRetry(() => import('@/pages/missions/MissionDetail').then(m => ({ default: m.MissionDetail })))

// User pages
const UsersPage = lazyWithRetry(() => import('@/pages/UsersPage').then(m => ({ default: m.UsersPage })))
const UserDetailPage = lazyWithRetry(() => import('@/pages/UserDetailPage').then(m => ({ default: m.UserDetailPage })))
// MembersPage removed — pending approvals handled on UsersPage, /members redirects to /users

// Analytics pages
const AnalyticsPage = lazyWithRetry(() => import('@/pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })))

// Settings pages
const SettingsPage = lazyWithRetry(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const ProfilePage = lazyWithRetry(() => import('@/pages/ProfilePage').then(m => ({ default: m.ProfilePage })))
const OrganizationSettingsPage = lazyWithRetry(() => import('@/pages/OrganizationSettingsPage').then(m => ({ default: m.OrganizationSettingsPage })))
const BillingPage = lazyWithRetry(() => import('@/pages/BillingPage').then(m => ({ default: m.BillingPage })))

// Waitlist pages
const WaitlistPage = lazyWithRetry(() => import('@/pages/waitlist/WaitlistPage').then(m => ({ default: m.WaitlistPage })))

// Admin pages (platform engineer dashboards)
const TemplateFeedbackDashboard = lazyWithRetry(() => import('@/pages/admin/TemplateFeedbackDashboard').then(m => ({ default: m.TemplateFeedbackDashboard })))

// Requested Features page (connector feature requests)
const RequestedFeaturesPage = lazyWithRetry(() => import('@/pages/requested-features/RequestedFeaturesPage').then(m => ({ default: m.RequestedFeaturesPage })))

// GAC pages
const GACPipelinePage = lazyWithRetry(() => import('@/pages/gac/GACPipelinePage').then(m => ({ default: m.GACPipelinePage })))

// Governance pages
const GovernancePage = lazyWithRetry(() => import('@/pages/GovernancePage').then(m => ({ default: m.GovernancePage })))
const GovernanceDesigner = lazyWithRetry(() => import('@/pages/governance/GovernanceDesigner').then(m => ({ default: m.GovernanceDesigner })))
const TrustPage = lazyWithRetry(() => import('@/pages/governance/TrustPage').then(m => ({ default: m.TrustPage })))
const ResourceManagementPage = lazyWithRetry(() => import('@/pages/governance/ResourceManagementPageV2').then(m => ({ default: m.ResourceManagementPageV2 })))
const ConnectorSelectionPage = lazyWithRetry(() => import('@/pages/governance/connectors/ConnectorSelectionPage').then(m => ({ default: m.ConnectorSelectionPage })))
const ConnectorFormPage = lazyWithRetry(() => import('@/pages/governance/connectors/ConnectorFormPage').then(m => ({ default: m.ConnectorFormPage })))
const SharePointSitesPage = lazyWithRetry(() => import('@/pages/governance/connectors/sharepoint-sites/SharePointSitesPage').then(m => ({ default: m.SharePointSitesPage })))
const ResourceDetailPage = lazyWithRetry(() => import('@/pages/governance/ResourceDetailPage').then(m => ({ default: m.ResourceDetailPage })))
const HubSpotOAuthCallbackPage = lazyWithRetry(() => import('@/pages/governance/HubSpotOAuthCallbackPage').then(m => ({ default: m.HubSpotOAuthCallbackPage })))
const AtlassianOAuthResultPage = lazyWithRetry(() => import('@/pages/governance/AtlassianOAuthResultPage').then(m => ({ default: m.AtlassianOAuthResultPage })))
const GitHubInstallationDetailPage = lazyWithRetry(() => import('@/pages/governance/GitHubInstallationDetailPage').then(m => ({ default: m.GitHubInstallationDetailPage })))
const ModelProviderHub = lazyWithRetry(() => import('@/pages/governance/ModelProviderHub').then(m => ({ default: m.ModelProviderHub })))
const AzureAIConnection = lazyWithRetry(() => import('@/pages/governance/MicrosoftFoundryConnection').then(m => ({ default: m.MicrosoftFoundryConnection })))
const AzureCognitiveConnection = lazyWithRetry(() => import('@/pages/governance/AzureCognitiveServicesConnection').then(m => ({ default: m.AzureCognitiveServicesConnection })))
const OpenAIConnection = lazyWithRetry(() => import('@/pages/governance/OpenAIConnection').then(m => ({ default: m.OpenAIConnection })))
const AnthropicConnection = lazyWithRetry(() => import('@/pages/governance/AnthropicConnection').then(m => ({ default: m.AnthropicConnection })))
const OpenRouterConnection = lazyWithRetry(() => import('@/pages/governance/OpenRouterConnection').then(m => ({ default: m.OpenRouterConnection })))
const ElevenLabsConnection = lazyWithRetry(() => import('@/pages/governance/ElevenLabsConnection').then(m => ({ default: m.ElevenLabsConnection })))
const GoogleVertexConnection = lazyWithRetry(() => import('@/pages/governance/GoogleVertexConnection').then(m => ({ default: m.GoogleVertexConnection })))
const CerebrasConnection = lazyWithRetry(() => import('@/pages/governance/CerebrasConnection').then(m => ({ default: m.CerebrasConnection })))
const MistralConnection = lazyWithRetry(() => import('@/pages/governance/MistralConnection').then(m => ({ default: m.MistralConnection })))
const HuggingFaceConnection = lazyWithRetry(() => import('@/pages/governance/HuggingFaceConnection').then(m => ({ default: m.HuggingFaceConnection })))
const VelmaConnection = lazyWithRetry(() => import('@/pages/governance/VelmaConnection').then(m => ({ default: m.VelmaConnection })))
const EscalationsPage = lazyWithRetry(() => import('@/pages/governance/EscalationsPage').then(m => ({ default: m.EscalationsPage })))
const AuditPage = lazyWithRetry(() => import('@/pages/governance/AuditPage').then(m => ({ default: m.AuditPage })))
const AuditMissionDetailPage = lazyWithRetry(() => import('@/pages/governance/AuditMissionDetailPage').then(m => ({ default: m.AuditMissionDetailPage })))
const AuditExecutionDetailPage = lazyWithRetry(() => import('@/pages/governance/AuditExecutionDetailPage').then(m => ({ default: m.AuditExecutionDetailPage })))
const ExecutionAuditRedirectPage = lazyWithRetry(() => import('@/pages/governance/ExecutionAuditRedirectPage').then(m => ({ default: m.ExecutionAuditRedirectPage })))
const AuditAssistantSessionDetailPage = lazyWithRetry(() => import('@/pages/governance/AuditAssistantSessionDetailPage').then(m => ({ default: m.AuditAssistantSessionDetailPage })))
const AuditDesignerDetailPage = lazyWithRetry(() => import('@/pages/governance/AuditDesignerDetailPage').then(m => ({ default: m.AuditDesignerDetailPage })))
const AuditIntelligenceCapsuleDetailPage = lazyWithRetry(() => import('@/pages/governance/AuditIntelligenceCapsuleDetailPage').then(m => ({ default: m.AuditIntelligenceCapsuleDetailPage })))
const AuditLearningDetailPage = lazyWithRetry(() => import('@/pages/governance/AuditLearningDetailPage').then(m => ({ default: m.AuditLearningDetailPage })))

// Tool pages
const ToolDetailPage = lazyWithRetry(() => import('@/pages/governance/ToolDetailPage').then(m => ({ default: m.ToolDetailPage })))
const ClassificationOverridesPage = lazyWithRetry(() => import('@/pages/governance/ClassificationOverridesPage').then(m => ({ default: m.ClassificationOverridesPage })))
const DataClassificationReviewPage = lazyWithRetry(() => import('@/pages/governance/DataClassificationReviewPage').then(m => ({ default: m.DataClassificationReviewPage })))

// CLI Tools pages — REMOVED: now integrated into ResourceManagementPageV2 Tools tab

// Intelligence layout (shared host selector state across all intelligence sub-routes)
const IntelligenceLayout = lazyWithRetry(() => import('@/pages/intelligence/components/IntelligenceLayout').then(m => ({ default: m.IntelligenceLayout })))

// Intelligence pages
const IntelligencePage = lazyWithRetry(() => import('@/pages/intelligence/IntelligencePage').then(m => ({ default: m.IntelligencePage })))
const CreateCapsulePage = lazyWithRetry(() => import('@/pages/governance/CapsuleWizardPage').then(m => ({ default: m.CapsuleWizardPage })))
// WS3a — simplified single "Add data" page, gated behind VITE_INGESTION_V2_ENABLED.
const IngestionAutoPage = lazyWithRetry(() => import('@/pages/governance/IngestionAutoPage').then(m => ({ default: m.IngestionAutoPage })))
// SP3 MR-c — reboot runtime ingestion page. Per register P24 (owner ruling
// 2026-07-03): when VITE_ENABLE_RUNTIME_INGESTION is on in an environment it
// REPLACES the capsules/new page there (IngestionAutoPage remains the
// flag-off default; legacy wizard stays at capsules/new/legacy).
const RuntimeIngestionPage = lazyWithRetry(() => import('@/pages/governance/RuntimeIngestionPage').then(m => ({ default: m.RuntimeIngestionPage })))
const CapsuleKindSelectionPage = lazyWithRetry(() => import('@/pages/intelligence/CapsuleKindSelectionPage').then(m => ({ default: m.CapsuleKindSelectionPage })))
const CompositeCapsulePage = lazyWithRetry(() => import('@/pages/intelligence/CompositeCapsulePage').then(m => ({ default: m.CompositeCapsulePage })))
const FileCapsulePage = lazyWithRetry(() => import('@/pages/intelligence/FileCapsulePage').then(m => ({ default: m.FileCapsulePage })))
// WS3b — 6-row capsule progress view, rendered at capsules/:capsuleId when the flag is on.
const CapsuleProgressView = lazyWithRetry(() => import('@/pages/governance/CapsuleProgressView').then(m => ({ default: m.CapsuleProgressView })))
const CapsuleDetailPage = lazyWithRetry(() => import('@/pages/intelligence/CapsuleDetailPage').then(m => ({ default: m.CapsuleDetailPage })))
const CapsuleEnrichmentReviewPage = lazyWithRetry(() => import('@/pages/intelligence/CapsuleEnrichmentReviewPage').then(m => ({ default: m.CapsuleEnrichmentReviewPage })))
const CapsuleDataTablePage = lazyWithRetry(() => import('@/pages/intelligence/CapsuleDataTablePage').then(m => ({ default: m.CapsuleDataTablePage })))
const CapsuleClassificationsPage = lazyWithRetry(() => import('@/pages/intelligence/CapsuleClassificationsPage').then(m => ({ default: m.CapsuleClassificationsPage })))
const AssembliesPage = lazyWithRetry(() => import('@/pages/intelligence/AssembliesPage').then(m => ({ default: m.AssembliesPage })))
const AssemblyDetailPage = lazyWithRetry(() => import('@/pages/intelligence/AssemblyDetailPage').then(m => ({ default: m.AssemblyDetailPage })))
const CapsuleManagementPage = lazyWithRetry(() => import('@/pages/intelligence/CapsuleManagementPage').then(m => ({ default: m.CapsuleManagementPage })))
// SP3 — retrieval quality evaluation (lens queries + corpus map), gated behind
// VITE_ENABLE_RETRIEVAL_TUNING (see plans/architecture/2026-07-03-sp3-admin-ui-port-plan.md).
const RetrievalPage = lazyWithRetry(() => import('@/pages/intelligence/RetrievalPage').then(m => ({ default: m.RetrievalPage })))
// SP3 — extraction rulebook design + fine-tuning pipeline, gated behind
// VITE_ENABLE_EXTRACTION_CURATION.
const FineTuningPage = lazyWithRetry(() => import('@/pages/intelligence/FineTuningPage').then(m => ({ default: m.FineTuningPage })))
// CreateDataCapsulePage removed — wizard consolidated under Intelligence → Capsules
// TaxonomyLibrarianPage removed as a standalone route — its content is
// now embedded as the Taxonomy tab on VocabularyPage. Old URLs redirect.
const VocabularyPage = lazyWithRetry(() => import('@/pages/intelligence/VocabularyPage').then(m => ({ default: m.VocabularyPage })))
const ResolutionPage = lazyWithRetry(() => import('@/pages/intelligence/ResolutionPage').then(m => ({ default: m.ResolutionPage })))
const GraphExplorerPage = lazyWithRetry(() => import('@/pages/intelligence/GraphExplorerPage').then(m => ({ default: m.GraphExplorerPage })))
const EntityResolutionPage = lazyWithRetry(() => import('@/pages/intelligence/EntityResolutionPage').then(m => ({ default: m.EntityResolutionPage })))
// Reboot identity spine (ADR-006 WP-I): the Go-runtime-backed workbench that
// replaces the legacy Rust-zone entity-resolution surface at cutover.
const IdentityWorkbenchPage = lazyWithRetry(() => import('@/pages/intelligence/IdentityWorkbenchPage').then(m => ({ default: m.IdentityWorkbenchPage })))

// Central Capsule Registry pages (Slices 10 & 11)
const AllCapsulesPage = lazyWithRetry(() => import('@/pages/intelligence/AllCapsulesPage').then(m => ({ default: m.AllCapsulesPage })))
const BackfillPage = lazyWithRetry(() => import('@/pages/intelligence/BackfillPage').then(m => ({ default: m.BackfillPage })))
const CapsuleRegistryDetailPage = lazyWithRetry(() => import('@/pages/intelligence/CapsuleRegistryDetailPage').then(m => ({ default: m.CapsuleRegistryDetailPage })))

// Intelligence Audit Log (plan: classifier-llm-assist.md Phase 3)

// Redirect helper that preserves a URL parameter (e.g., /agents/hosts/:hostId → /infrastructure/hosts/:hostId)
const RedirectWithParams: React.FC<{ to: string; paramName: string }> = ({ to, paramName }) => {
  const params = useParams()
  const value = params[paramName]
  return <Navigate to={value ? `${to}/${value}` : to} replace />
}

// The root ("/") landing (owner ruling 2026-09-30, F-NAV-01): a next-variant
// member lands on the business-reader overview; the current interface keeps
// its missions landing. The next shell renders while the variant resolves
// (agion/Agion#3210), and /overview also exists on the current interface, so
// a plain variant check is enough.
const RootLanding: React.FC = () => {
  const { variant } = useUIVariant()
  return <Navigate to={variant === 'next' ? '/overview' : '/missions'} replace />
}

export const AppRouter: React.FC = () => {
  // Once the authenticated shell is interactive, warm the lazy route chunks in
  // the background so navigating to a subpage hits the browser cache instead of
  // a cold fetch. Idle-scheduled and connection-aware, so it never competes
  // with the current page's data loading.
  useEffect(() => {
    prefetchRegisteredRoutes()
  }, [])

  return (
    <Routes>
      {/* Onboarding Routes - Outside MainLayout for full-screen experience */}
      <Route path="/onboarding/pending" element={
        <Suspense fallback={<PageLoader />}>
          <PendingApprovalPage />
        </Suspense>
      } />
      <Route path="/onboarding/create-organization" element={
        <Suspense fallback={<PageLoader />}>
          <CreateOrganizationPage />
        </Suspense>
      } />

      {/* Integration Callback Routes - Outside MainLayout */}
      <Route path="/integrations/github/callback" element={
        <Suspense fallback={<PageLoader />}>
          <GitHubCallbackPage />
        </Suspense>
      } />

      {/* Main Application Routes - Suspense handled by MainLayout */}
      <Route path="/" element={<VariantShell />}>
        <Route index element={<RootLanding />} />

        {/* Business-reader overview (owner ruling 2026-09-30, F-NAV-01): the
            next variant's landing page. The current interface has no
            equivalent destination, so this route mounts the missions page it
            already lands on until retirement. */}
        <Route path="overview" element={
          <ProtectedRoute requiredNavigation="missions">
            <PageWrapper><MissionCommand /></PageWrapper>
          </ProtectedRoute>
        } />

        {/* Legacy dashboard/control-center redirects */}
        <Route path="control-center" element={<Navigate to="/missions" replace />} />

        {/* Agent Management - Primary route */}
        <Route path="agents">
          <Route index element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><AgentsPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* Legacy redirects to /infrastructure */}
          <Route path="hosts" element={<Navigate to="/infrastructure/hosts" replace />} />
          <Route path="hosts/:hostId" element={<RedirectWithParams to="/infrastructure/hosts" paramName="hostId" />} />
          <Route path="host-groups" element={<Navigate to="/infrastructure/clusters" replace />} />
          <Route path="host-groups/:groupId" element={<RedirectWithParams to="/infrastructure/clusters" paramName="groupId" />} />
          <Route path=":id" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><AgentDetail /></PageWrapper>
            </ProtectedRoute>
          } />
        </Route>

        {/* Mission Management */}
        <Route path="missions">
          <Route index element={
            <ProtectedRoute requiredNavigation="missions">
              <PageWrapper><MissionCommand /></PageWrapper>
            </ProtectedRoute>
          } />
          {isMissionGalleryEnabled() && (
            <Route path="gallery" element={
              <ProtectedRoute requiredNavigation="missions">
                <PageWrapper><MissionGalleryPage /></PageWrapper>
              </ProtectedRoute>
            } />
          )}
          {isMissionGalleryEnabled() && (
            <Route path="planner" element={
              <ProtectedRoute requiredNavigation="missions">
                <PageWrapper><MissionPlannerPage /></PageWrapper>
              </ProtectedRoute>
            } />
          )}
          {/* Designer routes - must come before :id to match correctly */}
          <Route path="designer" element={
            <ProtectedRoute requiredNavigation="missions">
              <PageWrapper><MissionDesignerV4Page /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="designer/:id" element={
            <ProtectedRoute requiredNavigation="missions">
              <PageWrapper><MissionDesignerV4Page /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* A run's trace belongs to its mission: the server answers run
              evidence by mission view rights (agion/Agion#1987, #2588). */}
          <Route path=":missionId/executions/:executionId" element={
            <ProtectedRoute requiredNavigation="missions">
              <PageWrapper><ExecutionAuditRedirectPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* Mission details hub */}
          <Route path=":id" element={
            <ProtectedRoute requiredNavigation="missions">
              <PageWrapper><MissionDetail /></PageWrapper>
            </ProtectedRoute>
          } />
        </Route>

        {/* Governance & Policy Center */}
        <Route path="governance">
          <Route index element={
            <GovernanceRouteGuard requiredPermission="read">
              <PageWrapper><GovernancePage /></PageWrapper>
            </GovernanceRouteGuard>
          } />
          {/* Redirect /governance/rules to main governance page (atomic rules) */}
          <Route path="rules" element={<Navigate to="/governance" replace />} />
          <Route path="rules/manage" element={<Navigate to="/governance" replace />} />
          <Route path="labels" element={
            <GovernanceRouteGuard requiredPermission="read">
              <PageWrapper><GovernancePage /></PageWrapper>
            </GovernanceRouteGuard>
          } />
          {/* Policy authorship is admin-only on the server (RequireAdmin on rule writes).
              The next page answers a manager in place (F-POL-03); this
              redirect serves only the current interface, which mounts after
              the server's legacy answer. */}
          <Route path="designer" element={
            <GovernanceRouteGuard requiredPermission="admin">
              <PageWrapper><GovernanceDesigner /></PageWrapper>
            </GovernanceRouteGuard>
          } />
          {/* Classification (agion/Agion#2853) is a next-only page. The
              current interface has no equivalent destination, so this route
              mounts the Policy Center until retirement (as overview does). */}
          <Route path="classification" element={
            <GovernanceRouteGuard requiredPermission="admin" requiredNavigation="governance">
              <PageWrapper><GovernancePage /></PageWrapper>
            </GovernanceRouteGuard>
          } />
          <Route path="classification-overrides" element={
            <GovernanceRouteGuard requiredPermission="read" requiredNavigation="agents">
              <PageWrapper><ClassificationOverridesPage /></PageWrapper>
            </GovernanceRouteGuard>
          } />
          {/* ADR-052 data-source classification review */}
          <Route path="data-sources/:dataSourceId/classification" element={
            <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
              <PageWrapper><DataClassificationReviewPage /></PageWrapper>
            </GovernanceRouteGuard>
          } />
          {/* Redirect all old rule routes to governance page (atomic rules now used) */}
          <Route path="rules/*" element={<Navigate to="/governance" replace />} />
          <Route path="trust/*" element={
            <GovernanceRouteGuard requiredPermission="read">
              <PageWrapper><TrustPage /></PageWrapper>
            </GovernanceRouteGuard>
          } />
          {/* Where the Atlassian sign-in callback returns the admin with its
              result code (agion/Agion#3175); admin-only like the consent. */}
          <Route path="atlassian/oauth/result" element={
            <GovernanceRouteGuard requiredPermission="admin" requiredNavigation="resources">
              <PageWrapper><AtlassianOAuthResultPage /></PageWrapper>
            </GovernanceRouteGuard>
          } />
          <Route path="models" element={
            <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
              <CurrentResourceListRedirect page="models" />
            </GovernanceRouteGuard>
          } />
          <Route path="connections" element={
            <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
              <CurrentResourceListRedirect page="connections" />
            </GovernanceRouteGuard>
          } />
          <Route path="tools" element={
            <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
              <CurrentResourceListRedirect page="tools" />
            </GovernanceRouteGuard>
          } />
          <Route path="resources">
            <Route index element={
              <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
                <PageWrapper><ResourceManagementPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="models" element={
              <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
                <PageWrapper><ResourceManagementPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="prompts" element={
              <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
                <PageWrapper><ResourceManagementPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="connections" element={
              <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
                <PageWrapper><ResourceManagementPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="tools" element={
              <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
                <PageWrapper><ResourceManagementPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="tool/:toolId" element={
              <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
                <PageWrapper><ToolDetailPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="connection/github/:installationId" element={
              <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
                <PageWrapper><GitHubInstallationDetailPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            {/* A host's Agion-managed web address (agion/Agion#3473) is a
                next-only page; the current interface lands on the connections
                list until retirement. */}
            <Route path="connection/managed-address/:hostId" element={
              <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
                <PageWrapper><ResourceManagementPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="connection/:resourceId" element={
              <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
                <PageWrapper><ResourceDetailPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="hubspot/oauth/callback" element={
              <GovernanceRouteGuard requiredPermission="admin" requiredNavigation="resources">
                <PageWrapper><HubSpotOAuthCallbackPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="connect" element={
              <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                <PageWrapper><ConnectorSelectionPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="connect/sharepoint/sites" element={
              <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                <PageWrapper><SharePointSitesPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="connect/:connectorId" element={
              <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                <PageWrapper><ConnectorFormPage /></PageWrapper>
              </GovernanceRouteGuard>
            } />
            <Route path="providers">
              <Route index element={
                <GovernanceRouteGuard requiredPermission="read" requiredNavigation="resources">
                  <PageWrapper><ModelProviderHub /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              <Route path="azure-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><AzureAIConnection /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              <Route path="azure-cognitive-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><AzureCognitiveConnection /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              <Route path="openai-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><OpenAIConnection /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              <Route path="anthropic-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><AnthropicConnection /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              <Route path="openrouter-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><OpenRouterConnection /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              <Route path="elevenlabs-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><ElevenLabsConnection /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              <Route path="vertex-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><GoogleVertexConnection /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              <Route path="cerebras-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><CerebrasConnection /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              <Route path="mistral-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><MistralConnection /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              <Route path="huggingface-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><HuggingFaceConnection /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              <Route path="modulate-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><VelmaConnection /></PageWrapper>
                </GovernanceRouteGuard>
              } />
              {/* Cloudflare Workers AI is a next-only page: the provider came
                  after the current interface was retired. The current
                  interface has no equivalent destination, so this route
                  mounts the provider list until retirement (as
                  classification does). */}
              <Route path="cloudflare-connection" element={
                <GovernanceRouteGuard requiredPermission="write" requiredNavigation="resources">
                  <PageWrapper><ModelProviderHub /></PageWrapper>
                </GovernanceRouteGuard>
              } />
            </Route>
          </Route>
          <Route path="escalations" element={
            <ProtectedRoute requiredNavigation="governance">
              <PageWrapper><EscalationsPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* CLI Tools routes removed - now integrated into Resources > Tools tab */}
        </Route>

        {/* Intelligence — Context Management Interface */}
        {/* IntelligenceLayout is a layout route: mounts once, shares host selection
            state across all intelligence sub-routes via React context + Outlet */}
        <Route path="intelligence" element={<IntelligenceLayout />}>
          <Route index element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><IntelligencePage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="capsules" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><CapsuleManagementPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="capsules/new" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><CapsuleKindSelectionPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="capsules/new/connected" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper>{intelligenceFeatures.runtimeIngestion() ? <RuntimeIngestionPage /> : <IngestionAutoPage />}</PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="capsules/new/composite" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><CompositeCapsulePage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="capsules/new/files" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><FileCapsulePage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* Legacy wizard stays reachable for one full release cycle per plan §Rollback. */}
          <Route path="capsules/new/legacy" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><CreateCapsulePage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="capsules/:capsuleId" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><CapsuleProgressView /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* Legacy 7-tab detail page moved to /manage per plan §WS3b. */}
          <Route path="capsules/:capsuleId/manage" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><CapsuleDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* E7-S4: Enrichment quality feedback review page */}
          <Route path="capsules/:capsuleId/review" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><CapsuleEnrichmentReviewPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="capsules/:capsuleId/data/:tableName" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><CapsuleDataTablePage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="capsules/:capsuleId/classifications" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><CapsuleClassificationsPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* SP3: retrieval quality evaluation — registered only when the flag is on. */}
          {intelligenceFeatures.retrievalTuning() && (
            <Route path="retrieval" element={
              <ProtectedRoute requiredNavigation="agents">
                <PageWrapper><RetrievalPage /></PageWrapper>
              </ProtectedRoute>
            } />
          )}
          {/* SP3: extraction curation + fine-tuning — registered only when the flag is on. */}
          {intelligenceFeatures.extractionCuration() && (
            <Route path="fine-tuning" element={
              <ProtectedRoute requiredNavigation="agents">
                <PageWrapper><FineTuningPage /></PageWrapper>
              </ProtectedRoute>
            } />
          )}
          {/*
            Taxonomy lives under Vocabulary now (tab next to Entity Types
            and Link Types — same mission: how data is named/classified).
            Old `/intelligence/taxonomy` and `/intelligence/vocabulary/taxonomy`
            URLs redirect to the new tab so external links stay alive.
            The `LegacyVocabularyTaxonomyRedirect` previously bounced
            through a per-capsule manage page; it now points at the
            top-level Vocabulary tab directly.
          */}
          <Route path="taxonomy" element={<Navigate to="/intelligence/vocabulary?tab=taxonomy" replace />} />
          <Route path="vocabulary" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><VocabularyPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="vocabulary/taxonomy" element={<Navigate to="/intelligence/vocabulary?tab=taxonomy" replace />} />
          {/* SP2 C3 cutover-safety: the Resolution page + entity-resolution
              route family are served by the legacy Rust intelligence-zone
              (ResolutionRulesTable -> entity-resolution workbench client,
              EntityResolutionPage -> crosswalks client, both proxied to the
              zone). On a reboot host at cutover the legacyZoneSurfaces flag is
              off; the routes (and the redirects that fold into them) are
              dropped entirely so no page mounts and no probe hook fires against
              the retired zone — same treatment the taxonomy tab got in 0bfa993.
              A stale deep-link falls through to the global not-found instead of
              hard-failing 502/404 mid-render. */}
          {intelligenceFeatures.legacyZoneSurfaces() && (
            <>
              <Route path="resolution" element={
                <ProtectedRoute requiredNavigation="agents">
                  <PageWrapper><ResolutionPage /></PageWrapper>
                </ProtectedRoute>
              } />
              {/* Legacy redirects: the old /entities directory and the
                  standalone /review page both fold into /resolution now. */}
              <Route path="entities" element={<Navigate to="/intelligence/resolution" replace />} />
              <Route path="review" element={<Navigate to="/intelligence/resolution" replace />} />
            </>
          )}
          <Route path="graph" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><GraphExplorerPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="assemblies" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><AssembliesPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="assemblies/:compositionId" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><AssemblyDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* SP2 C3 cutover-safety: EntityResolutionPage's tabs read the
              crosswalks + entity-resolution zone family, so it is gated on the
              same legacyZoneSurfaces flag as /resolution above. */}
          {intelligenceFeatures.legacyZoneSurfaces() && (
            <Route path="entity-resolution" element={
              <ProtectedRoute requiredNavigation="agents">
                <PageWrapper><EntityResolutionPage /></PageWrapper>
              </ProtectedRoute>
            } />
          )}
          {/* Identity Workbench (ADR-006 WP-I): reboot Go-runtime-backed identity
              review surface. Always mounted (the reboot replacement for the
              legacy-gated entity-resolution page); its hooks are host-gated so it
              is inert until a host binds the runtime. */}
          <Route path="identity" element={
            <ProtectedRoute requiredPermission="admin" requiredNavigation="agents">
              <PageWrapper><IdentityWorkbenchPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* Central Capsule Registry (Slices 10 & 11) */}
          <Route path="registry" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><AllCapsulesPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="registry/:id" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><CapsuleRegistryDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {intelligenceFeatures.backfill() && (
            <Route path="backfill" element={
              <ProtectedRoute requiredNavigation="agents">
                <PageWrapper><BackfillPage /></PageWrapper>
              </ProtectedRoute>
            } />
          )}
          {/* Intelligence Audit Log — moved 2026-04-17 to Audit Log > Intelligence
              tab. This redirect preserves any bookmarked / dashboard links to
              the old path. Safe to drop the redirect after a deprecation window. */}
          <Route path="audit-log" element={<Navigate to="/audit?tab=intelligence" replace />} />
        </Route>

        {/* Infrastructure — Hosts, Clusters, Dashboard */}
        <Route path="infrastructure">
          <Route index element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><InfrastructureDashboardPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="hosts" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><HostsListPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="hosts/:hostId" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><HostDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="hosts/:hostId/pack-sync" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><EdgePackSyncStatusPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="clusters" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><ClustersListPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="clusters/new" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><CreateClusterWizardPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path="clusters/:clusterId" element={
            <ProtectedRoute requiredNavigation="agents">
              <PageWrapper><ClusterDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
        </Route>

        {/* Analytics & Insights */}
        <Route path="analytics">
          <Route index element={
            <ProtectedRoute requiredNavigation="analytics">
              <PageWrapper><AnalyticsPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* Advanced, cost and performance analytics were retired 2026-09-15 (mock-backed, no design);
              bookmarks land on the real destinations. */}
          <Route path="advanced" element={<Navigate to="/analytics" replace />} />
          <Route path="cost" element={<Navigate to="/cost" replace />} />
          <Route path="performance" element={<Navigate to="/analytics" replace />} />
        </Route>

        {/* User Management */}
        <Route path="users">
          <Route index element={
            <ProtectedRoute requiredNavigation="members">
              <PageWrapper><UsersPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path=":id" element={
            <ProtectedRoute requiredNavigation="members">
              <PageWrapper><UserDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
        </Route>

        {/* Members → Users redirect (legacy email links) */}
        <Route path="members" element={<Navigate to="/users" replace />} />

        {/* Settings & Profile */}
        <Route path="settings" element={
          <ProtectedRoute requiredNavigation="settings">
            <PageWrapper><SettingsPage /></PageWrapper>
          </ProtectedRoute>
        } />
        {/* Cost Management: the next variant lifts it onto its own route (PLAN-20260909 slice 8);
            the current page keeps the content inside Settings, so the same page serves here. */}
        <Route path="cost" element={
          <ProtectedRoute requiredNavigation="settings">
            <PageWrapper><SettingsPage /></PageWrapper>
          </ProtectedRoute>
        } />
        <Route path="organization" element={
          <ProtectedRoute requiredNavigation="organization">
            <PageWrapper><OrganizationSettingsPage /></PageWrapper>
          </ProtectedRoute>
        } />
        <Route path="profile" element={
          <ProtectedRoute>
            <PageWrapper><ProfilePage /></PageWrapper>
          </ProtectedRoute>
        } />
        <Route path="billing" element={
          <ProtectedRoute requiredNavigation="billing">
            <PageWrapper><BillingPage /></PageWrapper>
          </ProtectedRoute>
        } />
        <Route path="audit">
          <Route index element={
            <ProtectedRoute requiredNavigation="audit">
              <PageWrapper><AuditPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* Assistant session detail — static segment, ranks higher than :missionId */}
          <Route path="assistant/:sessionId" element={
            <ProtectedRoute requiredNavigation="audit">
              <PageWrapper><AuditAssistantSessionDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* Designer session detail — static segment, ranks higher than :missionId */}
          <Route path="designer/:sessionId" element={
            <ProtectedRoute requiredNavigation="audit">
              <PageWrapper><AuditDesignerDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* Intelligence capsule detail — static "intelligence" segment ranks
              higher than :missionId so /audit/intelligence/... does not
              collide with the bare mission-detail route. */}
          <Route path="intelligence/:hostId/:capsuleId" element={
            <ProtectedRoute requiredNavigation="audit">
              <PageWrapper><AuditIntelligenceCapsuleDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* WP-C10: Learning detail page — static "learnings" segment ranks
              higher than :missionId so /audit/learnings/... does not collide. */}
          <Route path="learnings/:learningId" element={
            <ProtectedRoute requiredNavigation="audit">
              <PageWrapper><AuditLearningDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
          <Route path=":missionId" element={
            <ProtectedRoute requiredNavigation="audit">
              <PageWrapper><AuditMissionDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
          {/* One run's trace: the mission's permission, not the audit list's
              (agion/Agion#2588); the server scopes the run (agion/Agion#1987). */}
          <Route path=":missionId/:executionId" element={
            <ProtectedRoute requiredNavigation="missions">
              <PageWrapper><AuditExecutionDetailPage /></PageWrapper>
            </ProtectedRoute>
          } />
        </Route>
        <Route path="executions/:executionId" element={
          <ProtectedRoute requiredNavigation="missions">
            <PageWrapper><ExecutionAuditRedirectPage /></PageWrapper>
          </ProtectedRoute>
        } />

        {/* Outcome help (PLAN-20261015 slice 08, A14) is a next-only page:
            entry points are managed in the next interface only. The current
            interface has no equivalent destination, so this route mounts the
            missions page it already lands on until retirement (as overview
            does). Any signed-in member may read it. */}
        <Route path="help/outcomes/:code" element={
          <ProtectedRoute>
            <PageWrapper><MissionCommand /></PageWrapper>
          </ProtectedRoute>
        } />

        {/* Waitlist Management (platform admin only) */}
        <Route path="waitlist" element={
          <ProtectedRoute requirePlatformAdmin>
            <PageWrapper><WaitlistPage /></PageWrapper>
          </ProtectedRoute>
        } />

        {/* Requested Features (platform admin only) */}
        <Route path="requested-features" element={
          <ProtectedRoute requirePlatformAdmin>
            <PageWrapper><RequestedFeaturesPage /></PageWrapper>
          </ProtectedRoute>
        } />

        {/* Admin pages (platform engineer dashboards) */}
        <Route path="admin">
          <Route path="template-feedback" element={
            <ProtectedRoute requirePlatformAdmin>
              <PageWrapper><TemplateFeedbackDashboard /></PageWrapper>
            </ProtectedRoute>
          } />
        </Route>

        {/* GAC Pipeline Monitoring */}
        {/* Platform operators only (agion/Agion#2588). */}
        <Route path="gac">
          <Route path="pipeline" element={
            <ProtectedRoute requirePlatformAdmin>
              <PageWrapper><GACPipelinePage /></PageWrapper>
            </ProtectedRoute>
          } />
        </Route>

        {/* Legacy Route Redirects for Backward Compatibility */}
        <Route path="policy-center/*" element={<Navigate to="/governance" replace />} />
        <Route path="mission-studio/*" element={<Navigate to="/missions" replace />} />
        <Route path="collaborators/*" element={<Navigate to="/users" replace />} />
        <Route path="insights/*" element={<Navigate to="/analytics" replace />} />
        <Route path="fleet/*" element={<Navigate to="/agents" replace />} />
      </Route>

      {/* Unauthorized - Access Denied */}
      <Route path="/unauthorized" element={
        <Suspense fallback={<PageLoader />}>
          <UnauthorizedPage />
        </Suspense>
      } />

      {/* 404 Not Found */}
      <Route path="*" element={
        <Suspense fallback={<PageLoader />}>
          <PageWrapper><NotFoundPage /></PageWrapper>
        </Suspense>
      } />
    </Routes>
  )
}
